import React, { useState, useEffect, useMemo } from 'react'
import "./BusinessHour.css";
import { useGetBusinessHoursMutation, useUpdateBusinessHoursMutation } from './api/businessHourApi';
import ReactTable from '../../components/ReactTable';
import Toast from '../../components/ToastNotification';
import { utcToLocalTime } from '../../utils/helpers';
import Select from 'react-select';
// const timezones = Intl.supportedValuesOf("timeZone");

const timeOptions = () => {
  const times = []; 
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const label = new Date(0, 0, 0, h, m)
        .toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      times.push({ label, value });
    }
  }
  times.push({
    label: "23:59", value: "23:59"
  });

  return times;
};

const weekdayToIndex = (dayName) =>
  ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"]
  .indexOf(dayName);

const mapWeekday = (dayIndex) => [
    "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
][dayIndex];

const toMinutes = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
};

const BusinessHourPage = () => {
  const [hours, setHours] = useState([]);
  const [prevHours, setPrevHours] = useState([]);
  const [is24x7, setIs24x7] = useState(false);
  const [timezone, setTimezone] = useState({
    label: "Asia/Kolkata",
    value: "Asia/Kolkata"
  });
  
  const timezoneList = useMemo(() => {
    return Intl.supportedValuesOf("timeZone").map(tz => ({
      label: tz,
      value: tz
    }));
  }, []);

  const columns = useMemo(() => [
    {
      header: "ACTIVE",
      accessorKey: "active",
      cell: ({ row }) => {  
        const i = row.index;
        return (
          <div className="day-info">
            <input
              type="checkbox"
              className="toggle"
              checked={row.original.active}
              disabled={is24x7}
              onChange={() => toggleDay(i)}
            />
          </div>
        );
      }
    },
    { header: "WEEK DAYS", accessorKey: "day" },
    {
      header: "START TIME",
      accessorFn: (row) => row.start_date,
      id: "start_time",
      cell: ({ row, getValue }) => {
        const i = row.index;
        return (
          row.original.active && (
            <select
              value={getValue()}
              className="time-select"
              disabled={is24x7}
              onChange={(e) =>
                updateTime(i, "start_date", e.target.value)
              }
            >
              {t.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )
        );
      },
    },
    {
      header: "END TIME",
      accessorFn: (row) => row.end_date,
      id: "end_time",
      cell: ({ row, getValue }) => {
        const i = row.index;
        return (
          row.original.active && (
            <select
              value={getValue()}
              className="time-select"
              disabled={is24x7}
              onChange={(e) =>
                updateTime(i, "end_date", e.target.value)
              }
            >
              {t.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            // <Select
            //   value={t.find((opt) => opt.value === getValue()) || null}
            //   options={t}
            //   isDisabled={is24x7}
            //   className="time-select"
            //   onChange={(selected) =>
            //     updateTime(i, "end_date", selected?.value)
            //   }
            // />
          )
        );
      },
    },
  ], [hours, is24x7]);

  const t = timeOptions();
  const [getBusinessHrsApi] = useGetBusinessHoursMutation();
  const [updateBusinessHrsApi] = useUpdateBusinessHoursMutation();

  const fetchBusinessHours = async () => {
    try {
      const response = await getBusinessHrsApi().unwrap();
      const res = response.data;
      if (res?.businessHours) {
        const bhData = res.businessHours.map(bh => ({
          id: bh.id,
          day: mapWeekday(bh.day_of_week),
          start_date:bh.is_active ? utcToLocalTime(bh.start_time).slice(0,5) : null,
          end_date:bh.is_active ? utcToLocalTime(bh.end_time).slice(0,5) : null,
          active: bh.is_active,
          is24x7: bh.is_24x7,
        }));
        setHours(bhData);
        setPrevHours(bhData);
        setIs24x7(bhData[0].is24x7);
        setTimezone({ label: res.timezone, value: res.timezone });
      }
    } catch (error) {
      
    }
  }

  const toggleDay = (i) => {
    const updated = [...hours];
    updated[i].active = !updated[i].active;
    updated[i].start_date = updated[i].active ? "09:00" : null;
    updated[i].end_date = updated[i].active ? "18:00" : null;

    setHours(updated);
  };

  const updateTime = (i, field, value) => {
    const updated = [...hours];
    const prevValue = updated[i][field];
    updated[i][field] = value;

    const start = updated[i].start_date;
    const end = updated[i].end_date;

    if (start && end && toMinutes(start) >= toMinutes(end)) {
      Toast.error(
        field === "start_date"
          ? "Start time must be earlier than End time."
          : "End time must be later than Start time."
      );
      updated[i][field] = prevValue;
      return;
    }   
    setHours(updated);
  };    

  const toggle24x7 = () => {
    if (!is24x7) {
      const fullTime = hours.map((d) => ({
        ...d,
        active: true,
        start_date: "00:00",
        end_date: "23:59",
      }));
      setHours(fullTime);
      setIs24x7(true);
    } else {
      setHours(prevHours);
      setIs24x7(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (hours.length === 0) {
        Toast.error("No business hours to update.");
        return;
      }

      const payload = {
        timezone: timezone.value,
        businessHours: hours.map((d) => ({
          id: d.id,
          day_of_week: weekdayToIndex(d.day),
          is_active: d.active,
          start_time: d.active ? d.start_date + ":00" : null,
          end_time: d.active ? d.end_date + ":00" : null,
          is24x7: is24x7
        }))
      };
      

      await updateBusinessHrsApi({ businessHours: payload }).unwrap();
      fetchBusinessHours();

      Toast.success("Business hours updated successfully.");
    } catch (error) {
      Toast.error(error.data?.message || "Failed to update business hours.");
    }
  }

  useEffect(() => {
    fetchBusinessHours();
  }, []);
  
  return (
    <div className="business-hour-page">
      <div className="business-hours-container">

        <div className='layout-header d-flex'>
          <h2>Business Hours</h2>
        </div>

        <div className="row"><hr /></div>

        <div className="timezone-box">
          <label >Select Timezone</label>
          <Select
            options={timezoneList}
            value={timezone}
            onChange={(selected) => setTimezone(selected)}
            className="timezone-select"
            classNamePrefix="react-select"
            placeholder="Select Timezone"
          />
        </div>

        <div className='layout-body'>
          <ReactTable
            columns={columns}
            data={hours}
          />
        </div>

        {/* ✅ 24×7 Toggle Section */}
        <div className="day-row" style={{ borderBottom: "none" }}>
          <label className="day-label">24×7 Mode</label>
          <input
            type="checkbox"
            className="toggle"
            checked={is24x7}
            onChange={toggle24x7}
          />
        </div>

        <button
          className="submit-btn"
          onClick={handleSubmit}
          // disabled={saving}
        >
          Submit
        </button>

      </div>
    </div>
    
  );
}

export default BusinessHourPage;
